import React from 'react';
import { BrowserRouter as Router, Link, Navigate, Route, Routes } from 'react-router-dom';
import AuthProvider from './auth/AuthProvider';
import useAuth from './auth/useAuth';
import Navbar from './components/Navbar';
import GlobalFooter from './components/GlobalFooter';
import { UnitProvider } from './units/UnitContext';
import ChangeEmailPage from './pages/ChangeEmailPage/ChangeEmailPage';
import ChangePasswordPage from './pages/ChangePasswordPage/ChangePasswordPage';
import ConfirmEmailChangePage from './pages/ConfirmEmailChangePage/ConfirmEmailChangePage';
import Home from './pages/Home';
import ForgotPasswordPage from './pages/ForgotPasswordPage/ForgotPasswordPage';
import Login from './pages/Login/Login';
import MaterialDatabase from './pages/MaterialDatabase/MaterialDatabase';
import MaterialList from './pages/MaterialDatabase/MaterialList';
import MaterialFinder from './pages/MaterialDatabase/MaterialFinder';
import MaterialComparison from './pages/MaterialDatabase/MaterialComparison';
import MaterialDetailPage from './pages/MaterialDatabase/MaterialDetailPage';
import HardnessComparison from './pages/MaterialDatabase/HardnessComparison';
import MaterialForm from './pages/MaterialDatabase/MaterialForm';
import MaterialTrash from './pages/MaterialDatabase/MaterialTrash';
import MaterialsHome from './pages/MaterialDatabase/MaterialsHome';
import MaterialPropertyGuide from './pages/MaterialDatabase/MaterialPropertyGuide';
import MaterialRequests from './pages/MaterialRequests/MaterialRequests';
import ProfilePage from './pages/ProfilePage/ProfilePage';
import RegisterPage from './pages/RegisterPage/RegisterPage';
import ResetPasswordPage from './pages/ResetPasswordPage/ResetPasswordPage';
import SupportPage from './pages/SupportPage/SupportPage';
import { ContactPage, CookiesPage, PrivacyPage, TermsPage } from './pages/LegalPages/LegalPages';
import LimitDeviationsPage from './pages/Tolerances/LimitDeviationsPage';
import FitsPage from './pages/Tolerances/FitsPage';
import FitFinderPage from './pages/Tolerances/FitFinderPage';
import RecommendedFitsPage from './pages/Tolerances/RecommendedFitsPage';
import GaugePage from './pages/Tolerances/GaugePage';
import ReamerCalculatorPage from './pages/Tolerances/ReamerCalculatorPage';
import Tolerances from './pages/Tolerances/Tolerances';
import TolerancesHome from './pages/Tolerances/TolerancesHome';
import Threads from './pages/Threads/Threads';
import ThreadsHome from './pages/Threads/ThreadsHome';
import MetricThreadPage from './pages/Threads/MetricThreadPage';
import VerifyEmailPage from './pages/VerifyEmailPage/VerifyEmailPage';
import './App.css';

const AdminMaterialFormRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading…</p>;
  }

  if (user?.role !== 'admin') {
    return <Navigate to="/materials" replace />;
  }

  return <MaterialForm />;
};

const AdminMaterialTrashRoute = () => {
  const { user, loading } = useAuth();
  if (loading) return <p>Loading…</p>;
  if (user?.role !== 'admin') return <Navigate to="/materials" replace />;
  return <MaterialTrash />;
};

const ProfileRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading profile...</p>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <ProfilePage />;
};
const SupportRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading support options...</p>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <SupportPage />;
};
const ChangeEmailRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <ChangeEmailPage />;
};
const ChangePasswordRoute = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <p>Loading...</p>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <ChangePasswordPage />;
};
const NotFound = () => (
  <section>
    <h1>Page not found</h1>
    <p>The requested page does not exist.</p>
    <Link to="/">Return to ASiJoLab</Link>
  </section>
);
const App = () => {
  return (
    <UnitProvider>
      <AuthProvider>
        <Router>
          <div className="appLayout">
            <Navbar />
            <main className="appContent">
              <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route
              path="/confirm-email-change"
              element={<ConfirmEmailChangePage />}
            />
            <Route path="/profile" element={<ProfileRoute />} />
            <Route path="/support" element={<SupportRoute />} />
            <Route path="/tolerances" element={<Tolerances />}>
              <Route index element={<TolerancesHome />} />
              <Route path="limit-deviations" element={<LimitDeviationsPage />} />
              <Route path="fits" element={<FitsPage />} />
              <Route path="fit-finder" element={<FitFinderPage />} />
              <Route path="recommended-fits" element={<RecommendedFitsPage />} />
              <Route path="gauges" element={<GaugePage />} />
              <Route path="reamer-calculator" element={<ReamerCalculatorPage />} />
            </Route>
            <Route path="/threads" element={<Threads />}>
              <Route index element={<ThreadsHome />} />
              <Route path="metric" element={<MetricThreadPage />} />
            </Route>
            <Route
              path="/profile/change-email"
              element={<ChangeEmailRoute />}
            />
            <Route
              path="/profile/change-password"
              element={<ChangePasswordRoute />}
            />
            <Route path="/materials" element={<MaterialDatabase />}>
              <Route index element={<MaterialsHome />} />
              <Route path="listofmaterials" element={<MaterialList />} />
              <Route path="hardnesscomparison" element={<HardnessComparison />} />
              <Route path="materialfinder" element={<MaterialFinder />} />
              <Route path="materialcomparison" element={<MaterialComparison />} />
              <Route path="properties/:propertySlug" element={<MaterialPropertyGuide />} />
              <Route path="requests" element={<MaterialRequests />} />
              <Route path="materialform" element={<AdminMaterialFormRoute />} />
              <Route path="trash" element={<AdminMaterialTrashRoute />} />
              <Route path=":materialSlug" element={<MaterialDetailPage />} />
            </Route>
            <Route path="*" element={<NotFound />} />
              </Routes>
            </main>
            <GlobalFooter />
          </div>
        </Router>
      </AuthProvider>
    </UnitProvider>
  );
};

export default App;
